import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useSetTestPartner } from "@/hooks/use-billing";
import { isTestPartner } from "@/lib/billing";
import { describeCallableError } from "@/lib/callables";
import { formatMonthYear } from "@/lib/format";
import type { SellerBillingStatus } from "@/types";

/**
 * O parceiro de teste: o estabelecimento cujas faturas, a partir do mês em
 * andamento, nascem ignoradas, sem a comissão do Zé Padoca.
 *
 * O flag mora em `sellersBillingStatus`, que só o backend escreve, e por isso
 * a troca passa por `adminSetTestPartner`, com confirmação: ela mexe na
 * cobrança do mês em andamento.
 */
export function TestPartnerCard({
  sellerId,
  status,
  loading,
}: {
  sellerId: string;
  status: SellerBillingStatus | null | undefined;
  loading: boolean;
}) {
  const setPartner = useSetTestPartner();
  // O valor que o administrador pediu, à espera da confirmação.
  const [confirming, setConfirming] = useState<boolean | null>(null);

  const active = isTestPartner(status);
  // Sem o status, o faturamento ainda não foi ativado pelo app do vendedor, e
  // o backend recusa: não há fatura para ignorar.
  const withoutBilling = !loading && !status;
  const now = new Date();
  const thisMonth = formatMonthYear(now.getMonth() + 1, now.getFullYear()).toLowerCase();
  const since = status?.testPartnerSince;
  const sinceLabel =
    since?.month && since?.year ? formatMonthYear(since.month, since.year).toLowerCase() : null;

  async function confirm() {
    if (confirming === null) return;
    try {
      const result = await setPartner.mutateAsync({ sellerId, isTestPartner: confirming });
      toast.success(
        result.message ??
          (confirming ? "Estabelecimento agora é parceiro de teste." : "Parceria de teste encerrada."),
      );
      setConfirming(null);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Parceiro de teste</CardTitle>
        <CardDescription>
          O parceiro de teste fica isento da taxa: as faturas dele nascem ignoradas, sem a comissão
          do Zé Padoca. O app do vendedor mostra a taxa calculada com o selo "Isento" e não gera
          Pix nem boleto para ela. Dívidas de antes da parceria continuam sendo cobradas.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <label className="flex items-center justify-between gap-4">
          <span className="min-w-0">
            <span className="block text-small font-semibold">Isento da taxa da plataforma</span>
            <span className="block text-caption text-muted-foreground">
              {active
                ? `Ligado desde a fatura de ${sinceLabel ?? "um mês sem registro"}.`
                : withoutBilling
                  ? "O faturamento deste estabelecimento ainda não foi ativado pelo app do vendedor."
                  : "Desligado: as faturas são cobradas normalmente."}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {setPartner.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            <Switch
              checked={active}
              disabled={loading || withoutBilling || setPartner.isPending}
              onCheckedChange={(next) => setConfirming(next)}
              aria-label="Parceiro de teste"
            />
          </span>
        </label>
      </CardContent>

      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>
            {confirming ? "Tornar parceiro de teste?" : "Encerrar a parceria de teste?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {confirming ? (
              <>
                A partir da fatura de {thisMonth}, as faturas deste estabelecimento são ignoradas,
                sem a comissão do Zé Padoca. As de meses anteriores ficam como estão; para dispensar
                alguma, use "Ignorar fatura" nela. Dívidas de antes da parceria continuam sendo
                cobradas.
              </>
            ) : (
              <>
                A partir da fatura de {thisMonth}, as faturas deste estabelecimento voltam a ser
                cobradas. As dos meses em que ele foi parceiro continuam ignoradas.
              </>
            )}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirm()} disabled={setPartner.isPending}>
              {setPartner.isPending ? <Loader2 className="animate-spin" /> : null}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
